import type { WorkspaceElement, CableRoute } from '@/types/synoptique'
import { PORT_COLORS } from '../constants'

/**
 * Calculate optimal position for export number label on a panel
 */
export function calculateExportNumberPosition(
  element: WorkspaceElement,
  connection: any,
  pageCables: CableRoute[]
): { x: number; y: number; textAnchor: 'start' | 'middle' | 'end'; fill: string } {
  const textColor = "#2D2D2D"

  if (!connection) {
    return {
      x: element.size.width / 2,
      y: element.size.height / 2,
      textAnchor: "middle",
      fill: textColor
    }
  }

  const cables = pageCables.filter(cable =>
    cable.from === element.id || cable.to === element.id
  )

  if (cables.length === 0) {
    return {
      x: element.size.width / 2,
      y: element.size.height / 2,
      textAnchor: "middle",
      fill: textColor
    }
  }

  const directions = {
    top: false,
    bottom: false,
    left: false,
    right: false
  }

  cables.forEach(cable => {
    const isIncoming = cable.to === element.id
    const otherPoint = isIncoming ? cable.points[0] : cable.points[1]
    const elementCenter = {
      x: element.size.width / 2,
      y: element.size.height / 2
    }

    const dx = otherPoint.x - (element.position.x + elementCenter.x)
    const dy = otherPoint.y - (element.position.y + elementCenter.y)

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) directions.right = true
      else directions.left = true
    } else {
      if (dy > 0) directions.bottom = true
      else directions.top = true
    }
  })

  const corners = [
    { name: 'topLeft', blocked: directions.top || directions.left, x: 8, y: 12, anchor: "start" as const },
    { name: 'topRight', blocked: directions.top || directions.right, x: element.size.width - 8, y: 12, anchor: "end" as const },
    { name: 'bottomLeft', blocked: directions.bottom || directions.left, x: 8, y: element.size.height - 8, anchor: "start" as const },
    { name: 'bottomRight', blocked: directions.bottom || directions.right, x: element.size.width - 8, y: element.size.height - 8, anchor: "end" as const }
  ]

  const bestCorner = corners.find(corner => !corner.blocked) || corners[0]

  return {
    x: bestCorner.x,
    y: bestCorner.y,
    textAnchor: bestCorner.anchor,
    fill: textColor
  }
}

/**
 * Create arrow marker for SVG export
 */
export function createArrowMarker(
  document: Document,
  markerId: string,
  color: string
): SVGMarkerElement {
  const arrowMarker = document.createElementNS("http://www.w3.org/2000/svg", "marker")
  arrowMarker.setAttribute("id", markerId)
  arrowMarker.setAttribute("markerWidth", "6")
  arrowMarker.setAttribute("markerHeight", "6")
  arrowMarker.setAttribute("refX", "5")
  arrowMarker.setAttribute("refY", "3")
  arrowMarker.setAttribute("orient", "auto")
  arrowMarker.setAttribute("markerUnits", "strokeWidth")

  const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
  arrowPath.setAttribute("d", "M 0 0 L 6 3 L 0 6 z")
  arrowPath.setAttribute("fill", color)
  arrowMarker.appendChild(arrowPath)

  return arrowMarker
}

/**
 * Create SVG export base element
 */
export function createExportSvgBase(): SVGSVGElement {
  const exportSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  exportSvg.setAttribute("width", "860mm")
  exportSvg.setAttribute("height", "600mm")
  exportSvg.setAttribute("viewBox", "-430 -300 860 600")
  exportSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  return exportSvg
}

/**
 * Convert SVG element to data URL for image export
 */
export async function svgToDataUrl(svg: SVGSVGElement): Promise<string> {
  return new Promise((resolve, reject) => {
    const svgString = new XMLSerializer().serializeToString(svg)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 860 * 2
      canvas.height = 600 * 2

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG image'))
    }

    img.src = url
  })
}

/**
 * Download SVG file
 */
export function downloadSvg(svg: SVGSVGElement, filename: string): void {
  const svgBlob = new Blob([new XMLSerializer().serializeToString(svg)], {
    type: 'image/svg+xml;charset=utf-8'
  })
  const url = URL.createObjectURL(svgBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Download PNG file from data URL
 */
export function downloadPng(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Get port colors for export (subset for legend)
 */
export const PORT_COLORS_EXPORT = PORT_COLORS.slice(0, 5)

/**
 * Get port color for export
 */
export function getPortColorForExport(portNumber: number): string {
  return PORT_COLORS_EXPORT[(portNumber - 1) % PORT_COLORS_EXPORT.length]
}
